import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * "Search within category" for the vertical filter panel.
 *
 * Typing narrows the visible filter values; submitting ticks the matches and hands off to
 * `facets-form-component.updateFilters()` — the same entry point a checkbox uses (see the
 * `on:change="/updateFilters"` binding in snippets/list-filter.liquid) — so results reload
 * exactly as they do for a normal filter click.
 *
 * Every value row carries `data-skip-node-update="true"`
 * (snippets/list-filter.liquid), which makes morph leave those nodes completely alone on a
 * re-render. Anything this component hides therefore stays hidden until it unhides it
 * itself — a section re-render will not clean up after it, and neither will clearing a
 * filter. So the narrowing is always undone before handing control back.
 *
 * @extends {Component<HTMLElement>}
 */
class FacetSearch extends Component {
  requiredRefs = ['input'];

  /** Panels this component opened, so they can be put back. @type {Set<HTMLDetailsElement>} */
  #opened = new Set();

  /** Rows currently hidden by this component. @type {Set<HTMLElement>} */
  #hidden = new Set();

  /** Cached row/label pairs; rebuilt whenever the results re-render. @type {{input: HTMLInputElement, row: HTMLElement, panel: HTMLElement | null, label: string}[] | null} */
  #index = null;

  /** @type {Element | null} */
  #form = null;

  connectedCallback() {
    super.connectedCallback();

    this.#form = this.closest('facets-form-component');

    // A filter clicked or cleared directly re-renders the results while a stale query may
    // still be narrowing the list. Capture phase so it runs before the facets form acts.
    this.#form?.addEventListener('change', this.#onFacetChange, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#form?.removeEventListener('change', this.#onFacetChange, true);
  }

  /** Debounced: re-querying every row on each keystroke is what made typing feel sticky. */
  handleInput = debounce(() => this.#narrow(this.refs.input.value), 120);

  /**
   * @param {KeyboardEvent} event
   */
  handleKeydown(event) {
    if (event.key === 'Enter') {
      // Without this the surrounding facets form submits and the page navigates away
      // before the filter is ever applied.
      event.preventDefault();
      this.submitSearch();
    } else if (event.key === 'Escape') {
      this.clearSearch();
    }
  }

  /** Applies the matching filter values. */
  submitSearch() {
    const query = this.refs.input.value.trim().toLowerCase();
    if (!query) return;

    const matches = this.#entries().filter((entry) => entry.label.includes(query));
    if (!matches.length) return;

    // An exact label match wins outright. Otherwise stay inside one filter group: ticking,
    // say, both Brands and Manufacturer Name ANDs them together and can leave the shopper
    // with nothing.
    const exact = matches.find((entry) => entry.label === query);
    const chosen = exact ? [exact] : matches.filter((entry) => entry.panel === matches[0].panel);

    // Undo the narrowing *before* the results reload, so the list the shopper comes back
    // to is whole — including after they later clear the filter.
    this.clearSearch();

    for (const entry of chosen) entry.input.checked = true;

    const form = /** @type {any} */ (this.#form);
    if (form && typeof form.updateFilters === 'function') form.updateFilters();
  }

  /** Empties the box and restores every row and panel this component touched. */
  clearSearch() {
    this.refs.input.value = '';

    for (const row of this.#hidden) row.hidden = false;
    this.#hidden.clear();

    for (const panel of this.#opened) panel.open = false;
    this.#opened.clear();

    for (const panel of this.#panels()) panel.hidden = false;

    this.dataset.searching = 'false';
  }

  // ---- internals -------------------------------------------------------------------

  #onFacetChange = (/** @type {Event} */ event) => {
    // Ticking a box in code does not fire `change`, so this only ever runs for a real
    // click — no risk of it undoing submitSearch's own selection.
    if (event.target === this.refs.input) return;
    if (this.dataset.searching === 'true') this.clearSearch();
    this.#index = null;
  };

  /** @returns {{input: HTMLInputElement, row: HTMLElement, panel: HTMLElement | null, label: string}[]} */
  #entries() {
    if (this.#index) return this.#index;

    const scope = this.closest('.facets') ?? document;
    const inputs = /** @type {HTMLInputElement[]} */ ([
      ...scope.querySelectorAll('input.checkbox__input[data-label]'),
    ]);

    this.#index = inputs
      .map((input) => {
        const row = input.closest('li') ?? input.closest('.checkbox');
        return row instanceof HTMLElement
          ? {
              input,
              row,
              panel: /** @type {HTMLElement | null} */ (input.closest('.facets__panel')),
              label: (input.dataset.label || '').trim().toLowerCase(),
            }
          : null;
      })
      .filter(/** @returns {x is NonNullable<typeof x>} */ (x) => x != null);

    return this.#index;
  }

  /** @returns {HTMLElement[]} */
  #panels() {
    const scope = this.closest('.facets') ?? document;
    return /** @type {HTMLElement[]} */ ([...scope.querySelectorAll('.facets__panel')]);
  }

  /**
   * Hides values that do not match, and any group left with nothing to show.
   * @param {string} raw
   */
  #narrow(raw) {
    const query = raw.trim().toLowerCase();

    if (!query) {
      this.clearSearch();
      return;
    }

    const entries = this.#entries();
    /** @type {Map<HTMLElement | null, boolean>} */
    const panelHasHit = new Map();

    for (const { row, panel, label } of entries) {
      const hit = label.includes(query);

      if (hit) {
        if (this.#hidden.delete(row)) row.hidden = false;
      } else if (!row.hidden) {
        row.hidden = true;
        this.#hidden.add(row);
      }

      panelHasHit.set(panel, (panelHasHit.get(panel) ?? false) || hit);
    }

    for (const panel of this.#panels()) {
      const hit = panelHasHit.get(panel) ?? false;
      panel.hidden = !hit;

      // Open a group that has hits so the matches are actually on screen.
      if (hit && panel instanceof HTMLDetailsElement && !panel.open) {
        panel.open = true;
        this.#opened.add(panel);
      }
    }

    this.dataset.searching = 'true';
  }
}

if (!customElements.get('facet-search')) {
  customElements.define('facet-search', FacetSearch);
}
